import React, { useEffect, useRef, useState } from 'react';
import { NavLink, Outlet, Link, useNavigate } from 'react-router-dom';
import { useUser, useClerk } from '@clerk/clerk-react';
import {
  LayoutDashboard, Users, RefreshCw, BarChart2, Building2,
  Activity, Package, ClipboardList, LogOut, UserCircle, ChevronRight, Server, Handshake,
} from 'lucide-react';
import { useApi } from '../../lib/api';
import { GreatLeapLogoIcon } from '../../components/GreatLeapLogoIcon';
import '../../styles/Admin.css';

const NAV_ITEMS = [
  { to: '/admin',               end: true, label: 'Overview',       icon: LayoutDashboard },
  { to: '/admin/users',                    label: 'Users',           icon: Users },
  { to: '/admin/reconciliation',           label: 'Reconciliation',  icon: RefreshCw },
  { to: '/admin/financial',                label: 'Financial',       icon: BarChart2 },
  { to: '/admin/tenants',                  label: 'Tenants',         icon: Building2 },
  { to: '/admin/usage',                    label: 'Usage',           icon: Activity },
  { to: '/admin/plans',                    label: 'Plans',           icon: Package },
  { to: '/admin/audit',                    label: 'Audit log',       icon: ClipboardList },
  { to: '/admin/server-health',            label: 'Server health',   icon: Server },
  { to: '/admin/partners',                 label: 'Partners',        icon: Handshake },
];

function userInitials(user) {
  if (!user) return '?';
  return ((user.firstName?.[0] || '') + (user.lastName?.[0] || '')).toUpperCase() || user.primaryEmailAddress?.emailAddress?.[0]?.toUpperCase() || '?';
}

export default function AdminLayout() {
  const api = useApi();
  const { user } = useUser();
  const { signOut } = useClerk();
  const navigate = useNavigate();

  const [me, setMe] = useState(null);
  const [error, setError] = useState(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);

  useEffect(() => {
    let alive = true;
    api.me()
      .then((r) => alive && setMe(r))
      .catch((err) => alive && setError(err?.response?.data?.error || err.message));
    return () => { alive = false; };
  }, [api]);

  useEffect(() => {
    const handleClick = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false);
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  if (error) return <div className="admin-banner error" style={{ margin: 32 }}>Failed to load admin: {error}</div>;
  if (!me)   return <div style={{ padding: 32, color: '#9ca3af' }}>Loading…</div>;
  if (!me.isPlatformAdmin) {
    return (
      <div style={{ padding: 32, maxWidth: 520, margin: '40px auto' }}>
        <h2 style={{ color: '#0f1c2e' }}>Platform admin access required</h2>
        <p style={{ color: '#6b7280' }}>This area is restricted to platform administrators.</p>
        <Link to="/dashboard" className="btn-primary" style={{ display: 'inline-flex', marginTop: 12 }}>
          Back to My Workspaces
        </Link>
      </div>
    );
  }

  const handleSignOut = async () => {
    await signOut();
    navigate('/signin');
  };

  return (
    <div className="admin-shell">
      <aside className="admin-sidebar">
        {/* Brand */}
        <Link to="/admin" className="admin-sidebar-brand">
          <GreatLeapLogoIcon size={32} />
        </Link>

        {/* Nav */}
        <nav style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 2, padding: '0 8px' }}>
          {NAV_ITEMS.map(({ to, end, label, icon: Icon }) => (
            <NavLink
              key={to}
              to={to}
              end={end}
              className={({ isActive }) => `admin-nav-link${isActive ? ' active' : ''}`}
            >
              <Icon size={15} />
              {label}
            </NavLink>
          ))}
        </nav>

        {/* User footer */}
        <div className="admin-sidebar-footer" ref={menuRef} style={{ position: 'relative' }}>
          <button className="admin-sidebar-user-btn" onClick={() => setMenuOpen((o) => !o)}>
            <div className="admin-user-avatar">{userInitials(user)}</div>
            <div style={{ minWidth: 0 }}>
              <div className="admin-user-name">{user?.firstName} {user?.lastName}</div>
              <div className="admin-user-email">{me.email}</div>
            </div>
            <ChevronRight size={13} style={{ marginLeft: 'auto', opacity: 0.4, flexShrink: 0 }} />
          </button>

          {menuOpen && (
            <div className="admin-user-menu">
              <Link to="/profile" onClick={() => setMenuOpen(false)}>
                <UserCircle size={13} style={{ marginRight: 6 }} />My Profile
              </Link>
              <Link to="/dashboard" onClick={() => setMenuOpen(false)} className="menu-apps">
                ← My Workspaces
              </Link>
              <button onClick={handleSignOut} className="menu-signout">
                <LogOut size={13} style={{ marginRight: 6 }} />Sign out
              </button>
            </div>
          )}
        </div>
      </aside>

      <main className="admin-main">
        <Outlet />
      </main>
    </div>
  );
}
